import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { EventDocumentType } from '@application/enums/event-document-type';
import { EventTypeEnum } from '@application/enums/event-type-enum';
import { setEventType } from '@application/features/f1Integration/f1Slice';
import { getAllVDKEventSortAndFilterAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { clearAnalytics } from '@application/features/eventAnalytics/event-analytics-slice';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Icon } from '@iconify/react';
import { Box, Button, Drawer, IconButton, Typography, useMediaQuery, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { StyledMenu } from '@ui/components/common/Menu';
import { PageLayout } from '@ui/components/PageLayout';
import dayjs from 'dayjs';
import { Star } from 'iconsax-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { hasPermission } from '@application/helpers/permission-helper';
import ResponsesCard from './components/ResponsesCard';
import ScheduledEventsCard from './components/ScheduledEventsCard';
import TotalResponsesCard from './components/TotalResponsesCard';
import useEventHomeStyles from './useEventHomeStyles';
const Events = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const styles = useEventHomeStyles();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));
    const [sortActive, setSortActive] = useState(false);
    const [filterActive, setFilterActive] = useState(true);
    const [eventId, setEventId] = useState('');
    const [previewDrawerOpen, setPreviewDrawerOpen] = useState(false);
    const [sortMenuAnchor, setSortMenuAnchor] = useState(null);
    const [selectedSort, setSelectedSort] = useState();
    const [searchQuery, setSearchQuery] = useState('');
    const { VDKEventsSortAndFilter } = useAppSelector((state) => state.VDKEvent);
    const dispatch = useAppDispatch();
    const loggedInUserPermissions = useSelector((state) => state.auth.currentUser?.permissions || []);
    const canCreateEvents = hasPermission(loggedInUserPermissions, 'Permissions.VdkEvents.Create');
    const currentUserRoles = useSelector((state) => state.auth.currentUser?.role);
    const isSfeermeester = Array.isArray(currentUserRoles) && currentUserRoles.includes('Sfeermeester');
    const handleFilter = () => {
        setFilterActive((prev) => !prev);
    };
    const handleSort = (event) => {
        setSortMenuAnchor(event.currentTarget);
        setSortActive(true);
    };
    const handleSortMenuClose = () => {
        setSortMenuAnchor(null);
        setSortActive(false);
    };
    const { responses: regularResponses } = useSelector((state) => state.eventAnalytics);
    const { responses: familyFestResponses } = useSelector((state) => state.familyFestAnalytics);
    const handleSortChange = (sortType) => {
        setSelectedSort(sortType);
        if (sortType === 'clear') {
            const defaultSortRequest = {
                documentType: EventDocumentType.BackgroundImage,
                sortByModified: false,
                sortByCreated: true,
                sortByEventDate: false
            };
            dispatch(getAllVDKEventSortAndFilterAsync(defaultSortRequest));
            setSelectedSort('creation');
            handleSortMenuClose();
            return;
        }
        const sortRequest = {
            documentType: EventDocumentType.BackgroundImage,
            sortByModified: sortType === 'modified',
            sortByCreated: sortType === 'creation',
            sortByEventDate: sortType === 'eventDate'
        };
        dispatch(getAllVDKEventSortAndFilterAsync(sortRequest));
        handleSortMenuClose();
    };
    // Sort menu options
    const sortOptions = [
        {
            id: 'creation',
            text: t('events.sort.creation'),
            group: true,
            to: () => handleSortChange('creation')
        },
        {
            id: 'modified',
            group: true,
            text: t('events.sort.modifiedDate'),
            to: () => handleSortChange('modified')
        },
        {
            id: 'eventDate',
            text: t('events.sort.eventDate'),
            group: true,
            to: () => handleSortChange('eventDate')
        }
    ];
    // Get selected sort text
    const getSelectedSortText = () => {
        switch (selectedSort) {
            case 'creation':
                return t('events.sort.creation');
            case 'modified':
                return t('events.sort.modifiedDate');
            case 'eventDate':
                return t('events.sort.eventDate');
            case 'clear':
                return t('events.sort.clearSorting') || 'Clear Sorting';
            default:
                return t('events.sortText');
        }
    };
    const selectedCompanyId = useSelector((state) => state.company.selectedCompanyId);
    useEffect(() => {
        if (!selectedCompanyId || selectedCompanyId.length == 0)
            return;
        // Clear analytics when company changes
        dispatch(clearAnalytics());
        // Clear selected event when company changes
        setEventId('');
        setEventName('');
        // Load events with default sorting by creation date
        const defaultSortRequest = {
            documentType: EventDocumentType.BackgroundImage,
            sortByModified: false,
            sortByCreated: true,
            sortByEventDate: false
        };
        dispatch(getAllVDKEventSortAndFilterAsync(defaultSortRequest));
        setSelectedSort('creation');
    }, [selectedCompanyId, dispatch]);
    const [eventName, setEventName] = useState('');
    const handleDataFromChild = (data) => {
        setEventName(data);
    };
    const handleIdFromChild = (id) => setEventId(id);
    const handlePreviewClick = () => {
        setPreviewDrawerOpen(true);
    };
    const scheduledEvents = useMemo(() => {
        // Use sorted events if available, otherwise fall back to regular events
        const eventsToUse = VDKEventsSortAndFilter;
        if (!eventsToUse?.length)
            return [];
        return eventsToUse.map((e) => {
            const backgroundImage = e.uploadedImages?.find((img) => img.isBackgroundImage);
            // Handle both Date objects and strings for date fields
            const startDate = e.startDate instanceof Date ? e.startDate : new Date(e.startDate);
            const endDate = e.endDate instanceof Date ? e.endDate : new Date(e.endDate);
            return {
                id: e.id,
                title: e.title,
                dateRange: dayjs(startDate).year() === dayjs(endDate).year()
                    ? `${dayjs(startDate).format('MMM D')} - ${dayjs(endDate).format('MMM D')}`
                    : `${dayjs(startDate).format('MMM D, YYYY')} - ${dayjs(endDate).format('MMM D, YYYY')}`,
                eventType: e.eventType,
                organizer: e.assignedUsers && e.assignedUsers.length > 0
                    ? e.assignedUsers.map((user) => ({
                        name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
                        avatar: `${user?.profilePictureUrl ?? '/placeholder.svg?height=24&width=24'}`,
                        firstName: user?.firstName ?? '',
                        lastName: user?.lastName ?? ''
                    }))
                    : [],
                companies: e.eventCompanies || [],
                image: `${backgroundImage?.url}`
            };
        });
    }, [VDKEventsSortAndFilter]);
    const filteredScheduledEvents = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query)
            return scheduledEvents;
        return scheduledEvents.filter((event) => event.title.toLowerCase().includes(query));
    }, [scheduledEvents, searchQuery]);
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };
    const handleSearchClear = () => {
        setSearchQuery('');
    };
    const selectedEvent = useMemo(() => {
        return filteredScheduledEvents.find((event) => event.id === eventId);
    }, [filteredScheduledEvents, eventId]);
    const responses = selectedEvent?.eventType === EventTypeEnum.Family ? familyFestResponses : regularResponses;
    return (_jsx(PageLayout, { padding: isSmallScreen ? false : undefined, title: t('common.events'), secondTitle: t('events.createFindOrOrganizeYourEventsHere'), sx: { overflowY: 'hidden' }, showLeftIcon: true, leftIcon: _jsx(Star, {}), rightContent: !isSfeermeester && (_jsx(Button, { startIcon: _jsx(Star, {}), variant: 'contained', size: isSmallScreen ? 'medium' : 'large', color: 'primary', disabled: !canCreateEvents, onClick: () => {
                if (canCreateEvents) {
                    navigate('/create-event');
                }
            }, children: t('events.newEvent') })), children: _jsx(Box, { id: 'page__eventHome', sx: { ...styles.main }, children: _jsxs(Box, { id: 'page__content-container', sx: { ...styles.contentContainer }, children: [_jsxs(Grid, { container: true, spacing: 2, sx: {
                            height: { xs: '100%', overflowY: 'auto' },
                            pr: 0.5
                        }, children: [_jsx(Grid, { size: { xs: 12, lg: 7, xl: 7 }, sx: {
                                // height: { lg: '60%', xs: 'auto' },
                                }, children: _jsx(ScheduledEventsCard, { events: filteredScheduledEvents, onFilter: handleFilter, onSort: handleSort, sortActive: sortActive, sendEventName: handleDataFromChild, filterActive: filterActive, selectedSortText: getSelectedSortText(), sendEventId: handleIdFromChild, onPreviewClick: handlePreviewClick, searchValue: searchQuery, onSearchChange: handleSearchChange, onSearchClear: handleSearchClear }) }), !isSmallScreen && (_jsx(_Fragment, { children: _jsxs(Grid, { size: { xs: 12, md: 12, lg: 5, xl: 5 }, sx: { height: { xs: 'auto' } }, children: [_jsx(Typography, { variant: 'subtitle2', color: 'textPrimary', sx: { mb: 1.5 }, children: eventName ? `Preview - ${eventName}` : 'Preview' }), _jsxs(Box, { sx: {
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 1,
                                                maxHeight: 'calc(100dvh - 163px)',
                                                overflowY: 'auto',
                                                pb: '10px'
                                            }, children: [_jsx(TotalResponsesCard, { totalResponses: responses?.totalResponses || 0, chartData: {
                                                        labels: responses?.dateSelected.map((item) => item.key) || [],
                                                        datasetLabel: 'Responses',
                                                        data: responses?.dateSelected?.map((item) => item.value) || []
                                                    } }), _jsx(Box, { children: _jsx(ResponsesCard, { eventId: eventId, isFamilyFest: selectedEvent?.eventType === EventTypeEnum.Family }) })] })] }) })), isSmallScreen && (_jsxs(Drawer, { anchor: 'bottom', open: previewDrawerOpen, onClose: () => setPreviewDrawerOpen(false), slotProps: {
                                    paper: {
                                        sx: {
                                            width: { xs: '100%' },
                                            height: '100%',
                                            p: 2,
                                            background: theme.palette.background.paper,
                                            display: 'flex',
                                            flexDirection: 'column'
                                        }
                                    }
                                }, children: [_jsxs(Box, { sx: {
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            mb: 2,
                                            flexShrink: 0
                                        }, children: [_jsx(Typography, { variant: 'h4', children: t('common.eventPreview') }), _jsx(IconButton, { size: 'small', color: 'default', onClick: () => setPreviewDrawerOpen(false), children: _jsx(Icon, { icon: 'ion:ios-arrow-down' }) })] }), _jsx(Box, { sx: {
                                            overflowY: 'auto',
                                            flexGrow: 1,
                                            mb: 2
                                        }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { size: { xs: 12 }, children: _jsxs(Typography, { variant: 'h5', children: [eventName, " ", t('actions.preview')] }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(TotalResponsesCard, { totalResponses: responses?.totalResponses || 0, chartData: {
                                                            labels: responses?.dateSelected.map((item) => item.key) || [],
                                                            datasetLabel: 'Responses',
                                                            data: responses?.dateSelected?.map((item) => item.value) || []
                                                        } }) }), _jsx(Grid, { size: { xs: 12 }, children: _jsx(ResponsesCard, { eventId: eventId, isFamilyFest: selectedEvent?.eventType === EventTypeEnum.Family }) })] }) }), _jsx(Box, { sx: {
                                            position: 'sticky',
                                            bottom: 0,
                                            backgroundColor: theme.palette.background.default,
                                            pt: 2,
                                            pb: 1,
                                            flexShrink: 0
                                        }, children: _jsx(Button, { variant: 'contained', color: 'primary', size: 'large', fullWidth: true, onClick: () => {
                                                if (!selectedEvent)
                                                    return;
                                                let url = `/event-details/${selectedEvent.id}`;
                                                if (selectedEvent.eventType &&
                                                    (selectedEvent.eventType === EventTypeEnum.Family ||
                                                        selectedEvent.eventType === EventTypeEnum.Director)) {
                                                    url += `/${selectedEvent.eventType}`;
                                                    dispatch(setEventType(selectedEvent.eventType));
                                                }
                                                navigate(url);
                                            }, disabled: !eventId || !selectedEvent, endIcon: _jsx(Icon, { icon: 'material-symbols:arrow-forward-ios-rounded' }), children: t('actions.viewDetails') }) })] }))] }), _jsx(StyledMenu, { anchorEl: sortMenuAnchor, open: Boolean(sortMenuAnchor), onClose: handleSortMenuClose, actions: sortOptions, selectedItem: selectedSort, setSelectedItem: setSelectedSort })] }) }) }));
};
export default Events;
